import { useEffect, useLayoutEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useRealWeddingReducer } from "../../../Utility";
import { onGetRealWedding } from "../../../Store/Slices/realWeddingSlice";
import {
  getSeoMeta,
  getCanonicalHref,

  normalizeSeoPathname,
} from "../../../config/seoMeta";
import { applySeoToDocument } from "../../../utils/seoDocument";
import "./RealWeddingDetailPage.css";

function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function sanitizeBlogHtml(html) {
  if (!html || typeof html !== "string") return "";
  const container = document.createElement("div");
  container.innerHTML = html;

  // Remove explicit word-break markers injected by editors.
  container.querySelectorAll("wbr").forEach((node) => node.remove());

  // Remove <br> tags only when they split a word (e.g. "prem<br>ium").
  container.querySelectorAll("br").forEach((node) => {
    const prevText = node.previousSibling?.textContent || "";
    const nextText = node.nextSibling?.textContent || "";
    const prevChar = prevText.slice(-1);
    const nextChar = nextText.charAt(0);
    if (/[A-Za-z0-9]/.test(prevChar) && /[A-Za-z0-9]/.test(nextChar)) {
      node.remove();
    }
  });

  const walk = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let textNode = walk.nextNode();
  while (textNode) {
    textNode.nodeValue = (textNode.nodeValue || "")
      .replace(/\u00a0/g, " ") // non-breaking space from editors
      .replace(/\u00ad/g, "") // soft hyphen
      .replace(/\u200b/g, "") // zero-width space
      .replace(/\u2060/g, "") // word joiner
      .replace(/\u200c/g, "") // zero-width non-joiner
      .replace(/\u200d/g, "") // zero-width joiner
      .replace(/\ufeff/g, "") // zero-width no-break space/BOM
      // Fix editor-inserted line breaks inside words: "Ghazia\nbad" -> "Ghaziabad"
      .replace(/([A-Za-z0-9])[\r\n\t]+([A-Za-z0-9])/g, "$1$2")
      .replace(/[ \t]{2,}/g, " ");
    textNode = walk.nextNode();
  }

  container.querySelectorAll("*").forEach((el) => {
    el.style.removeProperty("word-break");
    el.style.removeProperty("overflow-wrap");
    el.style.removeProperty("white-space");
    el.style.removeProperty("hyphens");
    el.style.removeProperty("line-break");
    el.style.removeProperty("display");
    el.style.removeProperty("width");
    el.style.removeProperty("min-width");
    el.style.removeProperty("max-width");
  });

  return container.innerHTML;
}

function buildBlogDocumentTitle(w) {
  const custom = (
    w?.metatitle ||
    w?.meta_title ||
    w?.metaTitle ||
    ""
  ).trim();
  if (custom) return custom;
  const h = stripHtml(w?.heading || "");
  if (h) return h;
  return "Wedding Blog";
}

function buildBlogMetaDescription(w) {
  const custom = (
    w?.metadescp ||
    w?.meta_description ||
    w?.meta_desc ||
    w?.metaDescription ||
    ""
  ).trim();
  if (custom) return custom;
  const plain = stripHtml(w?.description || "");
  if (plain) return plain.length > 300 ? `${plain.slice(0, 297)}…` : plain;
  return getSeoMeta("/blog").description;
}

function RealWeddingDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { getOneData: wedding, getOneLoading: loading, getOneError: error } = useRealWeddingReducer();

  const pathNorm = normalizeSeoPathname(location.pathname);
  const isBlogDetailRoute = /^\/blog\/[^/]+$/.test(pathNorm);

  useEffect(() => {
    if (!id) return;
    dispatch(onGetRealWedding(id));
  }, [id, dispatch]);

  useLayoutEffect(() => {
    if (!isBlogDetailRoute) return;

    if (loading) {
      applySeoToDocument({
        title: "Blog loading...",
        description: "Blog description loading...",
        robots: "index, follow",
        canonicalHref: getCanonicalHref(pathNorm),
        ogImage: "",
      });
      return;
    }

    if (!wedding) {
      const { title, description, robots } = getSeoMeta(pathNorm);
      applySeoToDocument({
        title,
        description,
        robots,
        canonicalHref: getCanonicalHref(pathNorm),
        ogImage: "",
      });
      return;
    }

    const slug = typeof wedding.slug === "string" ? wedding.slug.trim() : "";
    const canonicalPath = slug ? `/blog/${slug}` : pathNorm;
    const canonicalHref = getCanonicalHref(normalizeSeoPathname(canonicalPath));

    const photos = Array.isArray(wedding.wedding_photos) ? wedding.wedding_photos : [];
    const coverUrl =
      wedding.cover_photo &&
      typeof wedding.cover_photo === "string" &&
      !wedding.cover_photo.startsWith("C:\\")
        ? wedding.cover_photo
        : photos.find((p) => p?.image_type === "cover")?.image_url ?? photos[0]?.image_url;
    const ogImage =
      coverUrl && typeof coverUrl === "string" && !coverUrl.startsWith("C:\\") ? coverUrl : "";

    applySeoToDocument({
      title: buildBlogDocumentTitle(wedding),
      description: buildBlogMetaDescription(wedding),
      robots: "index, follow",
      canonicalHref,
      ogImage,
    });
  }, [isBlogDetailRoute, loading, wedding, pathNorm]);

  if (!id) {
    return (
      <div className="real-wedding-detail-page">
        <div className="real-wedding-detail-error">
          <p>Invalid wedding.</p>
          <Link to="/real-weddings" className="real-wedding-detail-back">
            ← Back to Real Weddings
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="real-wedding-detail-page">
        <div className="real-wedding-detail-skeleton">
          <div className="real-wedding-detail-skeleton-cover skeleton-pulse" />
          <div className="real-wedding-detail-skeleton-content">
            <div className="real-wedding-detail-skeleton-line skeleton-pulse" />
            <div className="real-wedding-detail-skeleton-line skeleton-pulse" />
            <div className="real-wedding-detail-skeleton-line real-wedding-detail-skeleton-line-short skeleton-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || (!loading && !wedding)) {
    return (
      <div className="real-wedding-detail-page">
        <div className="real-wedding-detail-error">
          <p>{error ? "Failed to load." : "Wedding not found."}</p>
          <Link to="/real-weddings" className="real-wedding-detail-back">
            ← Back to Real Weddings
          </Link>
        </div>
      </div>
    );
  }

  const isBlog = wedding?.content_type === "blog";
  const photos = Array.isArray(wedding.wedding_photos)
    ? [...wedding.wedding_photos].sort(
        (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
      )
    : [];

  const coverUrl =
    wedding.cover_photo && typeof wedding.cover_photo === "string" && !wedding.cover_photo.startsWith("C:\\")
      ? wedding.cover_photo
      : photos.find((p) => p?.image_type === "cover")?.image_url ?? photos[0]?.image_url;
  const sanitizedDescription = sanitizeBlogHtml(wedding.description || "");

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return "";
    }
  };

  const backLabel = isBlog ? "← Back to Real Weddings & Blog" : "← Back to Real Weddings";

  return (
    <div className={`real-wedding-detail-page ${isBlog ? "real-wedding-detail-page--blog" : ""}`}>
      <div className="real-wedding-detail">
        <Link to="/real-weddings" className="real-wedding-detail-back real-wedding-detail-back-top">
          {backLabel}
        </Link>

        {coverUrl && (
          <section className="real-wedding-detail-cover">
            {isBlog && wedding?.category && (
              <span className="real-wedding-detail-blog-tag">{wedding.category}</span>
            )}
            <img
              src={coverUrl}
              alt={wedding.heading || "Cover"}
              className="real-wedding-detail-cover-image"
            />
          </section>
        )}

        <div className="real-wedding-detail-content-wrap">
          {isBlog && (wedding?.published_at || wedding?.read_time_minutes != null || wedding?.author) && (
            <p className="real-wedding-detail-meta">
              {formatDate(wedding.published_at)}
              {(wedding.published_at && wedding.read_time_minutes != null) ? " · " : ""}
              {wedding.read_time_minutes != null ? `${wedding.read_time_minutes} min read` : ""}
              {wedding.author ? ` · By ${wedding.author}` : ""}
            </p>
          )}
          {wedding.heading && (
            <h1 className="real-wedding-detail-heading">{wedding.heading}</h1>
          )}

          {sanitizedDescription && (
            <section
              className="real-wedding-detail-description content"
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />
          )}

          {!isBlog && photos.length > 0 && (
            <section className="real-wedding-detail-gallery" aria-label="Wedding photos">
              {photos
                .filter((p) => p?.image_url && !String(p.image_url).startsWith("C:\\"))
                .map((photo, index) => (
                <figure key={index} className="real-wedding-detail-gallery-item">
                  <img
                    src={photo.image_url}
                    alt={`${wedding.heading || "Wedding"} ${index + 1}`}
                    className="real-wedding-detail-gallery-image"
                    loading={index < 3 ? "eager" : "lazy"}
                  />
                </figure>
              ))}
            </section>
          )}
        </div>

        <Link to="/real-weddings" className="real-wedding-detail-back real-wedding-detail-back-bottom">
          {backLabel}
        </Link>
      </div>
    </div>
  );
}

export default RealWeddingDetailPage;
